<template>
  <router-switch route-name="CrmContacts">
    <v-container fluid>
      <v-card class="sv-card pa-4">
        <v-row no-gutters>
          <data-table
            ref="table-hoc"
            v-model="selected"
            :height-offset="252"
            :filters.sync="filters"
            :schema="filterSchema"
            :headers="headers"
            :count="count"
            :loading="loading"
            :items="items"
            selectable-headers
            :table-options="dataTableProps"
            :column-select-props="{
              left: true
            }"
            :data-table-props="{
              showSelect: true
            }"
            @fetch="loadItems"
          >
            <template v-slot:before-filter>
              <v-btn
                v-if="$canAdd()"
                color="primary"
                depressed
                class="mr-4"
                :to="{ name: 'CrmContactsAdd' }"
              >
                <v-icon>mdi-plus</v-icon>
                CRM-контакт
              </v-btn>

              <v-menu :rounded="false" offset-y class="mr-4 flex-grow-0">
                <template v-slot:activator="{ attrs, on }">
                  <v-btn
                    color="primary"
                    :disabled="selected.length == 0"
                    depressed
                    class="mr-4"
                    v-bind="attrs"
                    v-on="on"
                  >
                    Групповые действия
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item
                    @click="massChange('companies', 'Компания', 'combobox')"
                  >
                    <v-list-item-title>Изменить компании</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="massChange('tags', 'Тэги', 'tags')">
                    <v-list-item-title>Изменить тэги</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="massChange('whatsdone', 'Что сделано', 'memo')"
                  >
                    <v-list-item-title>Изменить Что Сделано</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="massChange('comments', 'Комментарий', 'memo')"
                  >
                    <v-list-item-title>Изменить Комментарий</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-slot:after-filter>
              <v-btn
                depressed
                color="primary"
                outlined
                class="ml-4 sv-btn-icon"
                title="Экспорт в Excel"
                @click="exportCrm"
              >
                <v-icon>mdi-file-excel-outline</v-icon>
              </v-btn>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.title="{ item: { title, id } }">
              <router-link
                :to="{ name: 'CrmContactsEdit', params: { id } }"
                class="sv-toggler"
                >{{ title }}</router-link
              >
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.emails="{ item: { emails } }">
              <div
                v-for="(email, index) in emails.split(/\s+/)"
                :key="index"
                class="sv-toggle"
              >
                <a :href="`mailto:${email}`">{{ email }}</a>
              </div>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.node_created_at="{ item: { node_created_at } }">
              {{ formatDateTime(node_created_at) }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.tags="{ item: { tags } }">
              <badge
                v-for="(tag, tagIndex) in tags"
                :key="tagIndex"
                :text="tag"
              />
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.links.qr_code="{ item }">
              <span v-for="(id, index) in item.links.qr_code" :key="id">
                <span v-if="index > 0">&nbsp;</span>
                <router-link
                  :to="{
                    name: $can('QrGifts/edit') ? 'QrGiftsEdit' : 'QrGiftsView',
                    params: { id }
                  }"
                  class="sv-toggler no-border"
                >
                  {{ getDic("qr_codes", id, "id", "ident") }}
                </router-link>
              </span>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.source="{ item: { source } }">
              {{ getDic("crm_contact_sources", source, "term", "value") }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.contacted_user="{ item: { contacted_user } }">
              <div v-for="usr in contacted_user" :key="usr">
                {{ getDic("users", usr, "id", "name") }}
              </div>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.last_contact="{ item: { last_contact } }">
              {{ formatDate(last_contact) }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.next_contact="{ item }">
              <span :class="dateNextContactClass(item)">{{
                formatDate(item.next_contact)
              }}</span>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.account="{ item: { account } }">
              {{ getDic("users", account, "id", "name") }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.status="{ item: { status } }">
              <badge
                :text="getDic('crm_contact_statuses', status, 'term', 'value')"
                :status="getStatusColor(status)"
              />
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template #item.actions="{ item }">
              <action-col>
                <action-col-btn
                  v-if="$canViewHistory()"
                  :id="`btn-view-history-${item.id}`"
                  tooltip="История изменений"
                  @click="showHistory(item.id)"
                >
                  mdi-history
                </action-col-btn>
                <action-col-btn
                  v-if="$canEdit()"
                  :id="`btn-edit-${item.id}`"
                  tooltip="Изменить"
                  :to="{ name: 'CrmContactsEdit', params: { id: item.id } }"
                >
                  mdi-pencil-outline
                </action-col-btn>
                <action-col-btn
                  v-else
                  :id="`btn-view-${item.id}`"
                  tooltip="Просмотр"
                  :to="{ name: 'CrmContactsView', params: { id: item.id } }"
                >
                  mdi-eye-outline
                </action-col-btn>
                <action-col-btn
                  v-if="$canDelete()"
                  :id="`btn-delete-${item.id}`"
                  tooltip="Удалить"
                  @click="deleteNode(item.id)"
                >
                  mdi-close
                </action-col-btn>
              </action-col>
            </template>
          </data-table>

          <mass-assignment
            v-if="$canEdit()"
            v-model="massDialog"
            :field-name="massFieldName"
            :field-title="massFieldTitle"
            :dic="massFieldDic"
            :typ="massFieldTyp"
            @apply="massChangeExecute"
          />

          <mass-assignment-tags
            v-if="$canEdit()"
            v-model="massDialogTags"
            :field-name="massFieldName"
            :field-title="massFieldTitle"
            dic-name="qr_tags"
            @apply="massChangeExecute"
          />

          <history-object-modal
            v-if="$canViewHistory()"
            v-model="historyDialog"
            :item-object-id="selectedItemId"
            scope-name="Node"
          />
        </v-row>
      </v-card>
    </v-container>
  </router-switch>
</template>
<script>
import CheckPermissionMixin from "@/mixins/CheckPermissionMixin";
import NotificationMixin from "@/mixins/NotificationMixin";
import DialogMixin from "@/mixins/DialogMixin";
import QueryMixin from "@/mixins/QueryMixin";
import ResizeMixin from "@/mixins/ResizeMixin";

import SearchFilter from "@/components/Filters/SearchFilter";
import SelectFilter from "@/components/Filters/SelectFilter";
import DatetimeRangeFilter from "@/components/Filters/DatetimeRangeFilter";
import DataTable from "@/components/DataTable.vue";
import Badge from "@/components/Badge.vue";
import Repository from "@/api/special/crm-contacts.js";
import HistoryObjectModal from "@/components/HistoryObjectModal.vue";
import MassAssignment from "./../Components/MassAssignment.vue";
import MassAssignmentTags from "./../Components/MassAssignmentTags.vue";
import moment from "moment";
import { cloneDeep } from "lodash";

export default {
  components: {
    DataTable,
    HistoryObjectModal,
    Badge,
    MassAssignment,
    MassAssignmentTags
  },
  mixins: [
    CheckPermissionMixin,
    NotificationMixin,
    DialogMixin,
    QueryMixin,
    ResizeMixin
  ],
  data: () => ({
    given: 0,
    selected: [],
    selectedItemId: null,
    count: 0,
    options: {},
    loading: false,
    editDialog: false,
    filterDialog: false,
    historyDialog: false,
    massDialog: false,
    massDialogTags: false,
    massFieldName: "",
    massFieldTitle: "",
    massFieldDic: null,
    massFieldTyp: "text",
    filters: {},
    items: [],
    dics: {
      crm_contact_statuses: [],
      crm_contact_sources: [],
      qr_tags: [],
      qr_codes: [],
      users: []
    },
    initialQueryParams: {
      sort: [],
      page: 1,
      limit: 50
    }
  }),
  computed: {
    headers() {
      return [
        { text: "ID", value: "id" },
        { text: "Имя", value: "title" },
        { text: "Компания", value: "company" },
        { text: "Должность", value: "position" },
        { text: "Локация", value: "location" },
        { text: "Телефон", value: "phones" },
        { text: "Email", value: "emails" },
        { text: "Дата создания", value: "node_created_at" },
        { text: "Тэги", value: "tags" },
        { text: "Способ добавления", value: "source" },
        { text: "Кто контактировал", value: "contacted_user" },
        { text: "Сувенир", value: "links.qr_code" },
        { text: "Что сделано", value: "whats_done" },
        { text: "Комментарий", value: "comment" },
        { text: "Последний контакт", value: "last_contact" },
        { text: "Следующий контакт", value: "next_contact" },
        { text: "Аккаунт", value: "account" },
        { text: "Кол-во QR-заходов", value: "visit_cnt" },
        { text: "Последнии QR-заход", value: "visit_last" },
        { text: "Статус", value: "status" },
        {
          text: "Действия",
          value: "actions",
          sortable: false,
          align: "center",
          width: "155px"
        }
      ];
    },
    filterSchema() {
      const { dics } = this;
      return {
        residentData: {
          filters: {
            id: {
              component: SearchFilter,
              title: "ID",
              filterBind: { showMethod: false, like: false }
            },
            node_created_at: {
              component: DatetimeRangeFilter,
              title: "Дата создания"
            },
            title: {
              component: SearchFilter,
              title: "Имя",
              filterBind: { showMethod: true }
            },
            source: {
              component: SelectFilter,
              title: "Способ добавления",
              filterBind: {
                items: dics.crm_contact_sources,
                valueItem: "term",
                textItem: "value"
              }
            },
            company: {
              component: SearchFilter,
              title: "Компания",
              filterBind: { showMethod: true }
            },
            "links.qr_code": {
              component: SelectFilter,
              title: "Сувениры",
              filterBind: {
                items: dics.qr_codes,
                valueItem: "id",
                textItem: "text"
              }
            },
            position: {
              component: SearchFilter,
              title: "Должность",
              filterBind: { showMethod: true }
            },
            tags: {
              component: SelectFilter,
              title: "Тэги",
              filterBind: {
                items: dics.qr_tags,
                valueItem: "value",
                textItem: "term"
              }
            },
            phones: {
              component: SearchFilter,
              title: "Телефон",
              filterBind: { showMethod: true }
            },
            whats_done: {
              component: SearchFilter,
              title: "Что сделано",
              filterBind: { showMethod: true }
            },
            emails: {
              component: SearchFilter,
              title: "Email",
              filterBind: { showMethod: true }
            },
            comment: {
              component: SearchFilter,
              title: "Комментарий",
              filterBind: { showMethod: true }
            },
            location: {
              component: SearchFilter,
              title: "Локация",
              filterBind: { showMethod: true }
            },
            last_contact: {
              component: DatetimeRangeFilter,
              title: "Последний контакт"
            },
            contacted_user: {
              component: SelectFilter,
              title: "Кто контактировал",
              filterBind: {
                items: dics.users,
                valueItem: "id",
                textItem: "name"
              }
            },
            next_contact: {
              component: DatetimeRangeFilter,
              title: "Следующий контакт"
            },
            account: {
              component: SelectFilter,
              title: "Аккаунт",
              filterBind: {
                items: dics.users,
                valueItem: "id",
                textItem: "name"
              }
            },
            visit_last: {
              component: DatetimeRangeFilter,
              title: "Последнии QR-заход"
            },
            status: {
              component: SelectFilter,
              title: "Статус",
              filterBind: {
                items: dics.crm_contact_statuses,
                valueItem: "term",
                textItem: "value"
              }
            }
          }
        }
      };
    },
    getDic() {
      return (dic, value, id, term) => {
        const entry = this.dics[dic].find(item => item[id] == value);
        return entry?.[term] || "";
      };
    },
    getStatusColor() {
      return status => {
        switch (status) {
          case "contact":
            return "warning";
          case "lead":
            return "ok";
          default:
            return "neutral";
        }
      };
    },
    formatDate() {
      return date => (date ? moment(date).format("DD.MM.yyyy") : "");
    },
    formatDateTime() {
      return date => (date ? moment(date).format("DD.MM.yyyy HH:mm") : "");
    },
    dateNextContactClass() {
      return item => {
        if (!item.next_contact) return "";
        const diff = moment(item.next_contact).diff(
          moment().startOf("day"),
          "days"
        );
        return diff <= 0 && item.status !== "archive" ? "text--danger" : "";
      };
    },
    dataTableProps() {
      // по умолчанию
      const props = {
        sortBy: [],
        sortDesc: [],
        page: this.initialQueryParams.page,
        itemsPerPage: this.initialQueryParams.limit
      };

      // если специально указано по какому полю и как упорядочивать дочерние элементы
      if (this.initialQueryParams.sort.length > 0) {
        props.sortBy = [];
        props.sortDesc = [];
        this.initialQueryParams.sort.forEach(item => {
          props.sortBy.push(item[0]);
          props.sortDesc.push(item[1] == "desc");
        });
      }
      return props;
    },
    storageFiltersKey() {
      return `_SPEC_CRM_CONTACTS_QUERY`;
    }
  },
  watch: {
    given() {
      this.emitFetch();
    }
  },
  mounted() {
    this.loadQueryParams();
  },
  methods: {
    async loadItems(params) {
      try {
        this.loading = true;

        const { data } = await Repository.getList(params);
        this.dics = data.dics;
        this.dics.qr_codes = data.qrCodes;
        this.dics.users = data.users;
        this.items = data.items;
        this.count = data.count;
        this.$store.commit("setChildrenNodesCache", data.childrenCache);
        this.saveQueryParams(params);
      } finally {
        this.loading = false;
      }
    },
    async deleteNode(id) {
      const confirm = await this.confirm(
        "Удаление CRM-контакта",
        "Вы действительно хотите удалить этот контакт? Это действие нельзя отменить!",
        {
          width: 550
        }
      );
      if (!confirm) return;
      try {
        const { data } = await Repository.delete(id);
        this.emitFetch();
        this.sendAlert(data.alert);
      } catch (error) {
        return true;
      }
    },
    emitFetch() {
      this.$refs["table-hoc"].fetchData();
    },
    showHistory(id) {
      this.selectedItemId = id;
      this.historyDialog = true;
    },
    loadQueryParams() {
      const paramsStr = localStorage.getItem(this.storageFiltersKey);
      if (!paramsStr) return;
      const queryParams = JSON.parse(paramsStr);
      this.filters = { ...this.filters, ...queryParams.filters };
      if (queryParams.limit) this.initialQueryParams.limit = queryParams.limit;
      if (queryParams.page) this.initialQueryParams.page = queryParams.page;
      if (queryParams.sort) this.initialQueryParams.sort = queryParams.sort;
    },
    saveQueryParams(params) {
      localStorage.setItem(this.storageFiltersKey, JSON.stringify(params));
    },
    async exportCrm() {
      await Repository.exportCrm(this.getQueryOptions(), () => {});
    },
    async massChange(fieldName, fieldTitle, typ) {
      this.massFieldName = fieldName;
      this.massFieldTitle = fieldTitle;
      this.massFieldDic = null;
      if (fieldName == "companies") {
        const { data } = await Repository.getCreateTemplate();
        this.massFieldDic = data.companies;
      }
      this.massFieldTyp = typ;
      if (fieldName == "tags") {
        this.massDialogTags = true;
      } else {
        this.massDialog = true;
      }
    },
    async massChangeExecute(e) {
      try {
        const payload = cloneDeep(e.data);
        payload.selected = this.selected.map(item => item.id);

        this.loading = true;

        const { data } = await Repository.massUpdate(payload, e.fieldName);

        this.sendAlert(data.alert);

        this.selected = [];

        this.$store.dispatch("fetchProfile");

        this.emitFetch();
      } finally {
        this.loading = false;
      }
    },

    getDicByName(name) {
      const dic = this.$store.getters.getDicNames.find(dic => dic.name == name);
      if (dic) {
        return this.$store.getters.getDic(dic.id, "en");
      } else {
        return null;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/styles/branding.scss";
.text--danger {
  color: $brand-status-danger;
}
.sv-toggler {
  text-decoration: none !important;
  display: inline;
  line-height: 110%;
  color: #2196f3;
  border-bottom: 1px dashed #2196f3;
  cursor: pointer;
}
</style>
